<template>
  <v-container class="mt-4">
    <AppLoadingPage v-if="$apollo.queries.horse.loading" />
    <template v-else>
      <v-row dense>
        <v-col cols="12" md="6">
          <h2>
            {{
              $t("horses:horseProfilePage.ownershipSection.ownerSection.title")
            }}
          </h2>
        </v-col>
        <v-col cols="12" md="6" class="text-center text-md-end">
          <v-btn
            v-if="
              $allowedMutation(
                horse.allowedMutations,
                'horseGenerateOwnershipCertificate'
              )
            "
            color="edit"
            class="profile-btn mr-2"
            data-test="horseGenerateOwnershipCertificate"
            size="small"
            :loading="generatingCertificate"
            @click="horseGenerateOwnershipCertificate"
          >
            {{ $t("horses:owners.generateCertificate.button") }}
          </v-btn>
        </v-col>
      </v-row>

      <v-col cols="12" md="7">
        <v-card v-if="horse.ownerName" class="py-4 px-3">
          <v-row>
            <v-col cols="12" md="4" />

            <v-col md="8">
              <h3 class="mb-5">
                {{ horse.ownerName }}
              </h3>

              <div v-if="horse.ownershipSince">
                {{
                  $t(
                    "horses:horseProfilePage.ownershipSection.ownershipSince.label"
                  )
                }}
                {{ $dayjs.date(horse.ownershipSince) }}
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-divider class="my-3" />

      <v-row>
        <v-col>
          <h3>
            {{
              $t(
                "horses:horseProfilePage.ownershipSection.horseAdministratorsSection.title"
              )
            }}
          </h3>
        </v-col>
        <v-col md="4" class="text-center text-md-right">
          <v-btn
            v-if="canAddRole"
            dark
            size="small"
            color="edit"
            data-test="horseAdminAdd"
            @click="addItem"
          >
            {{
              $t(
                "horses:horseProfilePage.ownershipSection.addPersonRoleInHorse.button",
                "Add person (permissions)",
                "Add button"
              )
            }}
          </v-btn>
        </v-col>
      </v-row>

      <AppStaffList
        :staffs="horse.personRoles"
        no-edit
        data-test="ownerLists"
        :can-add="canAddRole"
      >
        <template #actions="{ item }">
          <v-btn-icon
            v-if="
              $allowedMutation(item.allowedMutations, 'personRoleTerminate')
            "
            size="small"
            class="bg-remove"
            @click="evt => terminatePersonRole(item, evt)"
            ><v-icon size="small" color="white">$iconTimesCircle</v-icon>
          </v-btn-icon>
          <v-btn-icon
            v-if="
              $allowedMutation(item.allowedMutations, 'personRevokeRoleInHorse')
            "
            size="small"
            class="bg-remove"
            data-test="staffRemove"
            :aria-label="$t('common:deleteButton.alt')"
            @click="evt => deletePersonRole(evt, item)"
            ><v-icon size="small" color="white">$iconTrash</v-icon>
          </v-btn-icon>
        </template>
      </AppStaffList>

      <template v-if="$allowedMutation(horse.allowedFields, 'horseContacts')">
        <v-divider class="my-3" />

        <v-card color="primary" class="fipa--header" tile>
          <v-row>
            <v-col class="d-flex align-center">
              {{
                $t(
                  "horses:horseProfilePage.ownershipSection.horseOwnersContactInformation.title"
                )
              }}
            </v-col>
            <v-col v-if="canAddEditContact" class="text-center text-md-right">
              <v-btn
                variant="text"
                color="white"
                class="btn-with-icon mx-2"
                :disabled="contactFormLoad"
                data-test="addPersonContact"
                @click="addPersonAsHorseContact()"
              >
                <div class="bg-edit btn-icon-with-text">
                  <v-icon size="small" class="text-white"> $iconPlus </v-icon>
                </div>
                <span class="text-white">{{
                  $t(
                    "horses:horseProfilePage.ownershipSection.horseOwnersContactInformation.addPerson.button"
                  )
                }}</span>
              </v-btn>
              <v-btn
                variant="text"
                color="white"
                class="btn-with-icon mx-2"
                :disabled="contactFormLoad"
                data-test="addOrganisationContact"
                @click="addOrganisationAsHorseContact()"
              >
                <div class="bg-edit btn-icon-with-text">
                  <v-icon size="small" class="text-white"> $iconPlus </v-icon>
                </div>
                <span class="text-white">{{
                  $t(
                    "horses:horseProfilePage.ownershipSection.horseOwnersContactInformation.addOrganisation.button"
                  )
                }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
        <v-data-table
          :items="horse.horseContacts"
          :headers="contactHeaders"
          data-test="horseContacts"
          :sort-by="['activeFrom']"
          sort-desc
          :loading="$apollo.queries.horse.loading"
          hide-default-footer
          :items-per-page="-1"
          :hide-default-header="$vuetify.display.smAndDown"
          class="entsys--table"
        >
          <template #item="{ item }">
            <tr>
              <td>
                <template
                  v-if="item.contactType == 'PERSON_REFERENCE' && item.person"
                >
                  <AppShowPersonName :person="item.person" />

                  <div v-if="item.person.feiid">
                    {{ item.person.feiid }}
                  </div>
                </template>
                <template v-else-if="item.contactType == 'PERSON_INFO'">
                  <div>
                    {{
                      showDisplayName({
                        firstName: item.firstName,
                        lastName: item.lastName
                      })
                    }}
                  </div>
                  <div v-if="item.feiid">
                    {{ item.feiid }}
                  </div>
                </template>
                <template
                  v-else-if="
                    item.contactType == 'ORGANISATION_REFERENCE' &&
                    item.organisation
                  "
                >
                  <AppShowOrganisationName :organisation="item.organisation" />

                  <br />
                  {{ item.organisationIdcode }}
                  <div v-if="item.organisation.feiid">
                    {{ item.organisation.feiid }}
                  </div>
                </template>
                <template v-else-if="item.contactType == 'ORGANISATION_INFO'">
                  {{ item.organisationName }}
                  <br />
                  {{ item.organisationIdcode }}

                  <div v-if="item.feiid">
                    {{ item.feiid }}
                  </div>
                </template>
              </td>
              <td>
                {{ $dayjs.date(item.activeFrom) }} -
                {{
                  item.activeUntil && item.activeUntil != "2199-01-01"
                    ? $dayjs.date(item.activeUntil)
                    : "..."
                }}
              </td>
              <td>
                {{ returnLocalisedPercent(item.ownershipShare) }}
              </td>
              <td>
                <template
                  v-if="
                    item.contactType == 'ORGANISATION_REFERENCE' &&
                    item.organisation &&
                    item.organisation.address
                  "
                >
                  <AppShowAddress :address="item.organisation.address" />
                </template>
                <template
                  v-else-if="
                    item.contactType == 'PERSON_REFERENCE' &&
                    item.person &&
                    item.person.address
                  "
                >
                  <AppShowAddress :address="item.person.address" />
                </template>
                <template v-else>
                  <AppShowAddress
                    :address="{
                      addressText: item.addressText,
                      addressIndex: item.addressIndex,
                      addressCity: item.addressCity,
                      addressBox: item.addressBox,
                      addressHouse: item.addressHouse,
                      addressCountry: item.addressCountry
                    }"
                  />
                </template>
              </td>
              <td>
                <template v-if="item.contactType == 'ORGANISATION_REFERENCE'">
                  {{ item.organisation.email }}<br />{{
                    item.organisation.phone
                  }}
                </template>
                <template v-else-if="item.contactType == 'PERSON_REFERENCE'">
                  {{ item.person.email }}<br />{{ item.person.phone }}
                </template>
                <template v-else>
                  {{ item.email }}<br />{{ item.phone }}
                </template>
              </td>
              <td>
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'horseContactCreateOrUpdate'
                    )
                  "
                  icon
                  size="small"
                  :disabled="contactFormLoad"
                  class="mx-1 bg-edit-green"
                  :aria-label="$t('common:editButton.alt')"
                  @click="editContact(item)"
                >
                  <v-icon color="white" size="small"> $edit </v-icon>
                </v-btn-icon>
                <v-btn-icon
                  v-if="
                    $allowedMutation(
                      item.allowedMutations,
                      'horseContactDiscard'
                    )
                  "
                  icon
                  size="small"
                  :disabled="contactFormLoad"
                  :loading="mutationLoadingId == item.id"
                  class="mx-1"
                  color="remove"
                  :aria-label="$t('common:deleteButton.alt')"
                  data-test="horseContactDelete"
                  @click="removeContact(item)"
                >
                  <v-icon color="white" size="small"> $iconTrash </v-icon>
                  <template #loader>
                    <v-progress-circular
                      color="white"
                      size="21"
                      indeterminate
                    />
                  </template>
                </v-btn-icon>
              </td>
            </tr>
          </template>
        </v-data-table>
      </template>
      <AppDialogContent
        v-if="canAddRole"
        v-model="showModal"
        :error="formError"
        :persistent="formSubmited"
        data-test="eventStaffForm"
        @submit-form="handleSubmit($event)"
      >
        <template #header>
          {{
            $t(
              "common:common.addstaff.label",
              "Add staff member",
              "Label of the add staff section"
            )
          }}
        </template>

        <div>
          <AppSelectRole
            v-model="form.role"
            :input-props="{
              errorMessages: fieldErrorMessages('role'),
              returnObject: true
            }"
            :filters="{
              haveScopes: ['RoleInHorse'],
              noSystem: true
            }"
            data-test="horseAdminRole"
            @blur="v$.form.role.$touch()"
          />
          <AppSelectPerson
            :key="showModal"
            v-model="form.personId"
            :input-props="{
              label: 'Person',
              errorMessages: fieldErrorMessages('personId')
            }"
            data-test="horseAdmin"
            @blur="v$.form.personId.$touch()"
          />
        </div>
      </AppDialogContent>

      <HorseOwnerForm
        v-if="canAddEditContact"
        ref="horseContact"
        v-model="showContactModal"
        use-dialog
        :loading="formSubmited"
        allow-showing-convert-to-reference
        :server-side-error="contactFromError"
        show-active-dates
        belongs-to-entity="HORSE"
        @add="submitContact($event)"
        @update="submitContact($event)"
      />
    </template>
  </v-container>
</template>
<script>
import { ref } from "vue";
import gql from "fraql";
import { get } from "lodash-es";

import { useFormValidation } from "@/composables/form/form_validation";
import AppShowAddress from "@AppShows/AppShowAddress.vue";

import { getFederationConfiguration } from "@composables/masterdata/config";
import AppStaffList from "@/components/elements/AppStaffList.vue";
import HorseOwnerForm from "./forms/HorseOwnerForm.vue";

import { useSaveMeta } from "@/composables/form/save_meta";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppLoadingPage from "@elements/AppLoadingPage.vue";
import AppSelectPerson from "@AppSelects/AppSelectPerson.vue";
import AppSelectRole from "@AppSelects/AppSelectRole.vue";
import AppShowPersonName from "@AppShows/person/AppShowPersonName.vue";
import AppShowOrganisationName from "@AppShows/organisation/AppShowOrganisationName.vue";
import { usePersonRoleActions } from "@/components/person/PersonRoles/personRoleActions";
import { showLoadingButton } from "@/utils/showLoadingButton";

const HORSE_CONTACT = gql`
fragment _ on HorseContact {
    id
    rolecode
    contactType
    feiid
    registryCountryId
    personId
    person {
      id
      name
      ${AppShowPersonName.fragments.person}
      firstName
      lastName
      feiid
      email
      phone
      address {
        id
        ${AppShowAddress.fragments.addressFields}
        addressCity
        addressText
        addressIndex
        addressHouse
        addressCountryId
        addressCountry @client {
          id
          name
        }
      }
    }
    firstName
    lastName
    organisationId
    organisation {
      id
      ${AppShowOrganisationName.fragments.organisation}
      name
      feiid
      businessRegCode
      email
      phone
      address { 
        id
        ${AppShowAddress.fragments.addressFields}
        addressCountryId
        addressCountry @client {
          id
          name
        }
        addressCity
        addressText
        addressIndex
        addressHouse
      }
    }
    organisationName
    organisationIdcode
    addressCountryId
    addressCountry @client {
      id
      name
    }
    addressCity
    addressText
    addressIndex
    addressHouse
    email
    phone
    activeFrom
    activeUntil
    ownershipShare
    allowedMutations
  }
`;
export default {
  name: "HorseOwners",
  components: {
    AppStaffList,
    AppDialogContent,
    AppSelectPerson,
    AppSelectRole,
    HorseOwnerForm,
    AppLoadingPage,
    AppShowPersonName,
    AppShowOrganisationName,
    AppShowAddress
  },
  props: {
    horseId: {
      type: String,
      required: true
    }
  },
  setup() {
    const { terminatePersonRole } = usePersonRoleActions();

    const form = ref({});
    const initRoleForm = ref({
      vuelidateValidations: {
        role: { required: true },
        personId: { required: true }
      },
      readOnlyFields: []
    });

    const { saveMetaSetOriginalForm, saveMetaConfirmUnsaveChanges } =
      useSaveMeta(null, { useOptionsAPIForm: true, usageInModal: true });

    const { v$, fieldDisabled, fieldErrorMessages, legacyFormErrorMessages } =
      useFormValidation(initRoleForm, form);

    return {
      ...getFederationConfiguration(),
      form,
      v$,
      initRoleForm,
      fieldDisabled,
      fieldErrorMessages,
      legacyFormErrorMessages,
      saveMetaSetOriginalForm,
      saveMetaConfirmUnsaveChanges,
      terminatePersonRole
    };
  },
  data() {
    return {
      horse: { horseContacts: [], personRoles: [] },

      showContactModal: false,
      showModal: false,
      formFeedbackText: null,
      formError: {},
      formContactError: null,
      formSubmited: false,
      contactFormLoad: false,
      contactFromError: null,
      mutationLoadingId: null,
      generatingCertificate: false
    };
  },
  computed: {
    canAddRole() {
      return this.$allowedMutation(
        this.horse.allowedMutations,
        "personGrantRoleInHorse"
      );
    },
    canAddEditContact() {
      return this.$allowedMutation(
        this.horse.allowedMutations,
        "horseContactCreateOrUpdate"
      );
    },
    contactHeaders() {
      return [
        {
          title: this.$t(
            "horses:horseProfilePage.ownershipSection.ownerName.label",
            "Owner"
          )
        },
        {
          title: this.$t(
            "horses:horseProfilePage.ownershipSection.ownerPeriod.label",
            "Period"
          )
        },
        {
          title: this.$t(
            "horses:horseProfilePage.ownershipSection.ownershipShare.label",
            "Shares"
          )
        },
        {
          title: this.$t(
            "horses:horseProfilePage.ownershipSection.ownerAddress.label",
            "Address"
          )
        },
        {
          title: this.$t(
            "horses:horseProfilePage.ownershipSection.ownerContacts.label",
            "Contact"
          )
        },
        { title: "" }
      ];
    }
  },
  watch: {
    async showModal(newVal) {
      if (newVal === false) {
        if ((await this.saveMetaConfirmUnsaveChanges()) === true) {
          this.showModal = false;
          this.form = {};
          this.saveMetaSetOriginalForm(this.form);
        } else {
          this.showModal = true;
        }
      }
    }
  },
  apollo: {
    horse: {
      query: gql`
        query horseAndContacts($id: ID!) {
          horse(id: $id) {
            id
            name
            ownerName
            ownershipSince
            allowedFields
            personRoles {
              id
              expiresAt
              person {
               ${AppStaffList.fragments.person}
              }
              role {
                id
                name
              }
              allowedMutations
            }
            horseContacts {
              ${HORSE_CONTACT}
            }
            allowedMutations
          }
        }
      `,
      variables() {
        return {
          id: this.horseId
        };
      }
    }
  },
  methods: {
    get,
    async horseGenerateOwnershipCertificate() {
      this.generatingCertificate = true;
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation horseGenerateOwnershipCertificate($id: ID!) {
              horseGenerateOwnershipCertificate(input: { id: $id }) {
                documentUrl
              }
            }
          `,
          variables: {
            id: this.horseId
          }
        });
        const blob = await fetch(
          response.data.horseGenerateOwnershipCertificate.documentUrl
        )
          .then(async res => await res.blob())
          .then(r => new Blob([r], { type: "application/pdf" }));

        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${this.horse.name}_certificate.pdf`;
        link.click();
      } catch (err) {
        this.$root.$snackbar("error", { text: err });
      } finally {
        this.generatingCertificate = false;
      }
    },

    addItem() {
      this.form = {
        horseId: this.horseId,
        role: null,
        personId: null
      };
      this.saveMetaSetOriginalForm(this.form);
      this.showModal = true;
      this.formError = {};
      this.v$.form.$reset();
    },
    addPersonAsHorseContact() {
      this.$refs.horseContact.setItemData({
        contactType: "PERSON_REFERENCE"
      });
      this.showContactModal = true;
    },
    addOrganisationAsHorseContact() {
      this.$refs.horseContact.setItemData({
        contactType: "ORGANISATION_REFERENCE"
      });
      this.showContactModal = true;
    },
    editContact(item) {
      this.$refs.horseContact.setItemData(item);
      this.showContactModal = true;
    },
    removeContact(item) {
      this.$root
        .$confirm(
          this.$t("horses:horseContact.deleteConfirm.heading"),
          this.$t("horses:horseContact.deleteConfirm.question")
        )
        .then(async confirm => {
          if (confirm) {
            this.contactFormLoad = true;
            this.mutationLoadingId = item.id;
            this.$apollo
              .mutate({
                mutation: gql`
                  mutation horseContactDiscard($id: ID!) {
                    horseContactDiscard(input: { id: $id }) {
                      horseContact {
                        id
                      }
                    }
                  }
                `,
                variables: { id: item.id },
                refetchQueries: ["horseAndContacts"]
              })
              .then(() => {
                this.contactFormLoad = false;
                this.$root.$snackbar("success", {
                  text: "Succesfully discarded!"
                });
                this.mutationLoadingId = null;
              })
              .catch(error => {
                this.$root.$snackbar("error", { text: error });
                this.contactFormLoad = false;
                this.mutationLoadingId = null;
              });
          }
        });
    },
    submitContact(form) {
      this.contactFormLoad = true;
      form.rolecode = "OWNER";
      form.horseId = this.horseId;
      if (form.ownershipShare)
        form.ownershipShare = parseFloat(form.ownershipShare);
      //this.contactForm.belongsToEntity = "HORSE";
      // $belongsToEntity: String
      // belongsToEntity: $belongsToEntity
      this.formSubmited = true;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation horseContactCreateOrUpdate(
              $id: ID
              $rolecode: String!
              $contactType: String!
              $horseId: ID
              $horseApplicationId: ID
              $personId: ID
              $firstName: String
              $lastName: String
              $organisationId: ID
              $organisationName: String
              $organisationIdcode: String
              $addressText: String
              $addressCity: String
              $addressIndex: String
              $addressHouse: String
              $addressCountryId: ID
              $registryCountryId: ID
              $feiid: String
              $phone: String
              $email: String
              $activeFrom: String
              $activeUntil: String
              $ownershipShare: Float
              $convertToReference: Boolean
            ) {
              horseContactCreateOrUpdate(
                input: {
                  id: $id
                  rolecode: $rolecode
                  contactType: $contactType
                  horseId: $horseId
                  horseApplicationId: $horseApplicationId
                  personId: $personId
                  firstName: $firstName
                  lastName: $lastName
                  organisationId: $organisationId
                  organisationName: $organisationName
                  organisationIdcode: $organisationIdcode
                  addressText: $addressText
                  addressCity: $addressCity
                  addressIndex: $addressIndex
                  addressHouse: $addressHouse
                  addressCountryId: $addressCountryId
                  registryCountryId: $registryCountryId
                  feiid: $feiid
                  phone: $phone
                  email: $email
                  activeFrom: $activeFrom
                  activeUntil: $activeUntil
                  ownershipShare: $ownershipShare
                  convertToReference: $convertToReference
                }
              ) {
                horseContact {
                  ${HORSE_CONTACT}
                }
              }
            }
          `,
          variables: form,
          refetchQueries: ["horseAndContacts"]
        })
        .then(() => {
          this.$root.$snackbar("success");
          this.contactFormLoad = false;
          this.formSubmited = false;
          this.showContactModal = false;
        })
        .catch(error => {
          // Error
          this.contactFromError = { type: "graphql", err: error };
          this.formSubmited = false;
          this.contactFormLoad = false;
        });
    },
    handleCancel() {
      this.showModal = false;
      this.formError = {};
      this.v$.form.$reset();
    },
    handleSubmit(evt) {
      const removeLoading = showLoadingButton(evt.target);
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        removeLoading();
        this.formError = this.legacyFormErrorMessages();
        return false;
      }
      this.formError = {};
      this.formFeedbackText = null;
      this.v$.form.$reset();
      this.formSubmited = true;
      // Mutation
      this.$apollo
        .mutate({
          mutation: gql`
            mutation personGrantRoleInHorse(
              $personId: ID!
              $roleId: ID!
              $horseId: ID!
            ) {
              personGrantRoleInHorse(
                input: {
                  personId: $personId
                  roleId: $roleId
                  horseId: $horseId
                }
              ) {
                horse {
                  id
                  personRoles {
                    id
                    person {
                      ${AppStaffList.fragments.person}
                    }
                    role {
                      id
                      name
                    }
                  }
                }
              }
            }
          `,
          variables: {
            personId: this.form.personId,
            horseId: this.horseId,
            roleId: this.form.role.id
          }
        })
        .then(() => {
          // Result
          this.form = {};
          this.saveMetaSetOriginalForm(this.form);
          this.showModal = false;
          this.$root.$snackbar("success");
          removeLoading();
          this.formSubmited = false;
        })
        .catch(error => {
          // Error
          this.formError = { type: "graphql", err: error };
          removeLoading();
          this.formSubmited = false;
          throw error;
        });
    },
    deletePersonRole(evt, personRole) {
      this.$root
        .$confirm(
          this.$t("horses:staffMember.deleteConfirm.heading"),
          this.$t(
            [
              "horses:staffMember.deleteConfirm.question",
              "Are you sure you want to remove permissions to manage horse {{horseName}}?"
            ],
            {
              horseName: this.horse.name
            }
          )
        )
        .then(async confirm => {
          if (confirm) {
            const removeLoading = showLoadingButton(evt.target);
            this.loading = true;
            this.$apollo
              .mutate({
                mutation: gql`
                  mutation personRevokeRoleInHorse(
                    $personRoleId: ID!
                    $horseId: ID!
                  ) {
                    personRevokeRoleInHorse(
                      input: { personRoleId: $personRoleId, horseId: $horseId }
                    ) {
                      horse {
                        id
                        personRoles {
                          id
                        }
                      }
                    }
                  }
                `,
                variables: {
                  personRoleId: personRole.id,
                  horseId: this.horseId
                },
                refetchQueries: ["horseAndContacts"]
              })
              .then(() => {
                removeLoading();
                this.$root.$snackbar("success", {
                  text: this.$t(
                    "common:feedbackMessage.removedSuccessfully.text",
                    "Succesfully deleted!"
                  )
                });
              })
              .catch(error => {
                removeLoading();
                this.$root.$snackbar("error", { text: error });
                throw error;
              });
          }
        });
    }
  }
};
</script>
